import { useEffect, useRef, useState } from 'react';
import { TrainingApi, SpeedReadingLessonStatus } from './trainingApi';

export interface LessonStatusEvent {
  status: SpeedReadingLessonStatus | 'NOT_FOUND';
  audioUrl?: string | null;
  audioDurationMs?: number | null;
  error?: string | null;
}

export const useLessonStatus = (lessonId: string | null, enabled: boolean) => {
  const [event, setEvent] = useState<LessonStatusEvent | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const sourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!lessonId || !enabled) {
      setEvent(null);
      return;
    }

    let cancelled = false;
    const startPolling = () => {
      if (fallbackTimerRef.current) return;
      const tick = async () => {
        try {
          const lesson = await TrainingApi.get(lessonId);
          if (!cancelled) {
            setEvent({
              status: lesson.status,
              audioUrl: lesson.audioMedia?.url || null,
              audioDurationMs: lesson.audioDurationMs || null,
              error: lesson.processingError || null,
            });
            if (lesson.status !== 'PROCESSING' && lesson.status !== 'DRAFT') {
              if (fallbackTimerRef.current) {
                window.clearInterval(fallbackTimerRef.current);
                fallbackTimerRef.current = null;
              }
            }
          }
        } catch (err) {
          /* ignore polling errors silently */
        }
      };
      void tick();
      fallbackTimerRef.current = window.setInterval(tick, 5000);
    };

    if (typeof window === 'undefined' || typeof window.EventSource === 'undefined') {
      startPolling();
      return () => {
        cancelled = true;
        if (fallbackTimerRef.current) {
          window.clearInterval(fallbackTimerRef.current);
          fallbackTimerRef.current = null;
        }
      };
    }

    const url = TrainingApi.buildEventsUrl(lessonId);
    const source = new EventSource(url);
    sourceRef.current = source;

    source.onmessage = (msg) => {
      try {
        const data = JSON.parse(msg.data) as LessonStatusEvent;
        setEvent(data);
      } catch {
        /* ignore */
      }
    };

    source.onerror = () => {
      try { source.close(); } catch (_) { /* noop */ }
      sourceRef.current = null;
      if (!cancelled) startPolling();
    };

    return () => {
      cancelled = true;
      try { source.close(); } catch (_) { /* noop */ }
      sourceRef.current = null;
      if (fallbackTimerRef.current) {
        window.clearInterval(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [lessonId, enabled]);

  return event;
};
